import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MIcon from "../icons/MIcon";
import SpinnerIcon from "../icons/SpinnerIcon";
import MessageBanner from "../components/ui/MessageBanner";
import Select from "../components/ui/Select";
import KpiStrip from "../components/analytics/KpiStrip";
import CallsPerDayCard from "../components/analytics/CallsPerDayCard";
import CallsByHourCard from "../components/analytics/CallsByHourCard";
import CallsByBusinessCard from "../components/analytics/CallsByBusinessCard";
import AppointmentsCard from "../components/analytics/AppointmentsCard";
import RecentCallsCard from "../components/analytics/RecentCallsCard";
import { useAuth } from "../AuthContext";
import { analyticsAPI } from "../api";
import { getUserErrorMessage } from "../utils/errors";

const RANGE_VALUES = ["today", "7d", "30d", "90d", "all"];

async function fetchAnalytics({ range, orgId }) {
  const res = await analyticsAPI.getOverview({ range, orgId });
  return res.data;
}

const Analytics = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const rangeOptions = RANGE_VALUES.map((value) => ({
    value,
    label: t(`analytics.ranges.${value}`),
  }));

  const [range, setRange] = useState("7d");
  const [orgId, setOrgId] = useState(""); // "" = all orgs (admins only)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ text: "", type: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchAnalytics({
        range,
        orgId: orgId === "" ? null : Number(orgId),
      });
      setData(payload);
    } catch (err) {
      setError(err);
      setMessage({ text: getUserErrorMessage(err, "load"), type: "error" });
    } finally {
      setLoading(false);
    }
  }, [range, orgId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    // Fill the viewport height (= sidebar height) and turn the page into
    // a vertical flex container so the header stays pinned and only the
    // dashboard body scrolls internally.
    <div className="h-screen flex flex-col">
      {/* ── Sticky page header (does not scroll with the content) ── */}
      <div className="px-4 sm:px-8 pt-10 pb-6 border-b border-surface-light-highest dark:border-white/5 bg-app-bg dark:bg-app-bg">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-brand dark:bg-brand flex items-center justify-center shadow-brand-glow">
              <MIcon name="monitoring" className="text-white !text-[24px]" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-semibold text-on-surface-light dark:text-on-surface tracking-tight">
                {t("analytics.title")}
              </h1>
              <p className="text-sm text-on-surface-light-variant dark:text-on-surface-variant">
                {isAdmin
                  ? t("analytics.subtitleAdmin")
                  : t("analytics.subtitleOrg")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Select
              icon="date_range"
              value={range}
              onChange={setRange}
              options={rangeOptions}
              disabled={loading}
              ariaLabel={t("analytics.timeRange")}
            />
            {isAdmin &&
              data?.available_orgs &&
              data.available_orgs.length > 0 && (
                <Select
                  icon="corporate_fare"
                  value={orgId}
                  onChange={setOrgId}
                  options={data.available_orgs}
                  placeholder={t("analytics.allOrgs")}
                  disabled={loading}
                  ariaLabel={t("analytics.organization")}
                  className="min-w-[180px]"
                />
              )}
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface transition-colors disabled:opacity-40"
              aria-label={t("analytics.refreshAria")}
            >
              {loading ? (
                <SpinnerIcon />
              ) : (
                <MIcon name="refresh" className="sm" />
              )}
              <span>{t("common.refresh")}</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Scrollable dashboard body ──
          flex-1 + min-h-0 lets the inner div consume the remaining viewport
          height (min-h-0 is required for the overflow-auto child to actually
          clip instead of letting flex grow forever). */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-8 py-6">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* ── Initial loading ── */}
          {loading && !data && (
            <div className="flat-card rounded-xl animate-float-in">
              <div className="p-16 flex items-center justify-center text-on-surface-light-variant dark:text-on-surface-variant">
                <SpinnerIcon />
                <span className="ml-2 text-sm">{t("analytics.loading")}</span>
              </div>
            </div>
          )}

          {/* ── Error fallback ── */}
          {error && !loading && <MessageBanner message={message} />}

          {/* ── Dashboard content ── */}
          {data && !error && (
            <>
              <KpiStrip kpis={data.kpis} />

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <CallsPerDayCard
                  className="lg:col-span-2"
                  series={data.calls_per_day}
                />
                <CallsByHourCard matrix={data.calls_by_hour} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <CallsByBusinessCard
                  className="lg:col-span-2"
                  rows={data.calls_by_business}
                />
                <AppointmentsCard appointments={data.appointments} />
              </div>

              <RecentCallsCard rows={data.recent_calls} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
